import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Theme store — light | dark | system. Dark mode řídí `.dark` třída na
 * <html> (UUI foundation `globals.css` na ni váže tokeny). Perzistence do
 * localStorage; `system` sleduje `prefers-color-scheme`.
 */
export type ThemeMode = "light" | "dark" | "system";

interface ThemeState {
  mode: ThemeMode;
  /** Aktuální systémová preference — drží se ve storu, aby na ni šlo reagovat v renderu. */
  systemDark: boolean;
  setMode: (mode: ThemeMode) => void;
  setSystemDark: (systemDark: boolean) => void;
}

const MEDIA = "(prefers-color-scheme: dark)";

function resolveDark(mode: ThemeMode): boolean {
  if (mode === "system") {
    return typeof window !== "undefined" && window.matchMedia(MEDIA).matches;
  }
  return mode === "dark";
}

function applyDark(mode: ThemeMode): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolveDark(mode));
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: "system",
      systemDark: resolveDark("system"),
      setMode: (mode) => {
        applyDark(mode);
        set({ mode });
      },
      setSystemDark: (systemDark) => set({ systemDark }),
    }),
    // Persistuje se jen volba uživatele; `systemDark` je stav prostředí, který se čte znovu
    // při každém startu (na jiném stroji nebo po přenastavení OS by uložená hodnota lhala).
    { name: "etts-theme", partialize: (state) => ({ mode: state.mode }) },
  ),
);

/**
 * Skutečně vykreslený režim. `system` sám o sobě neříká, jestli je tmavý — a ikona
 * v horní liště se musí přepnout i tehdy, když si uživatel přehodí motiv v OS.
 */
export function useResolvedDark(): boolean {
  return useThemeStore((s) => (s.mode === "system" ? s.systemDark : s.mode === "dark"));
}

/** Aplikuje uložené téma při startu + naváže listener na změnu systémového režimu. */
export function initTheme(): void {
  if (typeof window === "undefined") return;
  const store = useThemeStore.getState();
  applyDark(store.mode);
  store.setSystemDark(resolveDark("system"));
  window.matchMedia(MEDIA).addEventListener("change", (event) => {
    useThemeStore.getState().setSystemDark(event.matches);
    if (useThemeStore.getState().mode === "system") applyDark("system");
  });
}
